import { useState } from 'react';
import api from '../services/api';

/**
 * @module useUpdateWeeklyAiReport
 * @description Custom hook for updating weekly AI reports in the Mental Health Portal application.
 * Manages updating state, error handling, and provides a function for updating weekly AI reports with optional resource recommendations.
 *
 * @requires react
 * @requires ../services/api
 */

/**
 * Hook to update weekly AI reports
 * @function useUpdateWeeklyAiReport
 * @returns {Object} Object containing updateWeeklyAiReport function, isUpdating state, and error state
 */
export const useUpdateWeeklyAiReport = () => {
  /**
   * @state {boolean} isUpdating - Indicates if the update operation is in progress
   */
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * @state {string|null} error - Error message if the update operation fails, null otherwise
   */
  const [error, setError] = useState(null);

  /**
   * Updates a weekly AI report with new content and optional resource recommendation
   * @async
   * @function updateWeeklyAiReport
   * @param {Object} params - Parameters for updating the report
   * @param {string|number} params.id - ID of the report to update
   * @param {string} params.newAiReport - New AI report content
   * @param {string} params.userId - ID of the user making the update
   * @param {number} [params.resourceId] - Optional ID of the recommended resource
   * @returns {Promise<Object>} The updated report data
   * @throws {Error} If the update operation fails
   */
  const updateWeeklyAiReport = async ({
    id,
    newAiReport,
    userId,
    resourceId,
  }) => {
    setIsUpdating(true);
    setError(null);

    try {
      console.log(
        'id,newAiReport,userId,resourceId',
        id,
        newAiReport,
        userId,
        resourceId
      );
      const { data } = await api.post('update-weekly-ai-report', {
        id,
        newAiReport,
        userId,
        ...(resourceId !== undefined && { resourceId }), // only send if defined
      });

      return data;
    } catch (err) {
      console.error('Erro ao atualizar relatório:', err);
      setError('Erro ao atualizar relatório semanal');
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * @typedef {Object} UseUpdateWeeklyAiReportReturn
   * @property {Function} updateWeeklyAiReport - Function to update a weekly AI report
   * @property {boolean} isUpdating - Indicates if the update operation is in progress
   * @property {string|null} error - Error message if the update operation fails, null otherwise
   */

  /**
   * @returns {UseUpdateWeeklyAiReportReturn} The hook's return values
   */
  return { updateWeeklyAiReport, isUpdating, error };
};
